import { formatISO } from 'date-fns';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ParticipantsItemCollapseFields } from './ParticipantsItemCollapse.enums';
import { FieldProps } from './ParticipantsItemCollapse.types';
import {
  getShortFormatDateTimeFromIsoString,
  LONG_DAY_NUM_MONTH_LONG_YEAR_SLASHES,
} from 'basics/utils/dates/dates.utils';
import { Market, Presence, Source } from 'generated/event.types';
import { Participant } from 'modules/events/basics/types/events.types';

const FIELDS = (t: TFunction, eventPresence: Presence, eventSource?: Source): FieldProps[] => ([
  {
    key: ParticipantsItemCollapseFields.mdmId,
    label: t('person_field_mdmId_label'),
  },
  {
    key: ParticipantsItemCollapseFields.accountId,
    label: t('person_field_accountId_label'),
  },
  {
    key: ParticipantsItemCollapseFields.guestCountryCode,
    label: t('person_field_guestCountry_label'),
  },
  {
    key: ParticipantsItemCollapseFields.phone,
    label: t('person_field_phone_label'),
  },
  {
    key: ParticipantsItemCollapseFields.collectedPhoneNumber,
    label: t('person_field_collectedPhoneNumber_label'),
  },
  {
    key: ParticipantsItemCollapseFields.specialty,
    label: t('person_field_specialty_label'),
  },
  {
    key: ParticipantsItemCollapseFields.zoomCallLink,
    label: t('person_field_eventLink_label'),
  },
  {
    key: ParticipantsItemCollapseFields.invitationDate,
    label: t('person_field_invitationDate_label'),
  },
  {
    key: ParticipantsItemCollapseFields.invitationEmailSent,
    label: t('person_field_invitationEmailSent_label'),
  },
  ...(eventSource === Source.veeva ? [{
    key: ParticipantsItemCollapseFields.externalEventParticipantStatus,
    label: t('person_field_veeva_status_label'),
  }] : []),
]);

const formatStatusInvitedDate = (date: Date | null | undefined) => {
  if (!date) {
    return null;
  }
  const formatted = formatISO(new Date(date));
  return getShortFormatDateTimeFromIsoString(formatted, LONG_DAY_NUM_MONTH_LONG_YEAR_SLASHES);
};

const formatValue = (field: FieldProps, participant: Participant, t: TFunction) => {
  const { key } = field;
  const {
    invitationDate,
    invitationEmailSent,
    guestCountryCode,
  } = ParticipantsItemCollapseFields;

  switch (key) {
    case invitationDate:
      return participant.registrationStatusHistory.invited
        ? formatStatusInvitedDate(participant.registrationStatusHistory.invited)
        : null;
    case invitationEmailSent:
      return participant[key] ? t('common_yes') : t('common_no');
    case guestCountryCode:
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return new Intl.DisplayNames(['en'], { type: 'region' }).of(participant[key] as string);
      } catch (err) {
        return participant[key];
      }
    default:
      return participant[key];
  }
};

const filterFields = (fields: FieldProps[], market?: Market | null) => {
  if (market !== Market.com) {
    return fields.filter((field) => field.key !== ParticipantsItemCollapseFields.guestCountryCode);
  }
  return fields;
};

const useParticipantsItemCollapse = (
  participant: Participant,
  eventPresence = Presence.online,
  marketTag?: Market,
  eventSource?: Source,
) => {
  const { t } = useTranslation();

  const fieldsConfig = useMemo(() => {
    const fields = FIELDS(t, eventPresence, eventSource);
    const filteredFields = filterFields(fields, marketTag);
    return filteredFields.map((field) => ({
      ...field,
      value: formatValue(field, participant, t),
    }));
  },
  [t, eventPresence, marketTag, eventSource, participant]);

  return {
    fieldsConfig,
  };
};

export default useParticipantsItemCollapse;
